import React, { useState, useEffect } from 'react';
import { ChevronRight, Home, RefreshCw, Download } from 'lucide-react';
import { apiService } from '../../services/api.service';
import { toast } from 'react-hot-toast';

type LevelType = 'DISTRICT' | 'MANDAL' | 'VILLAGE' | 'BENEFICIARY';

interface DrillLevel {
  type: LevelType;
  district?: string;
  mandal?: string;
  village?: string;
}

const PennyDropDrillDown: React.FC = () => {
  const [currentLevel, setCurrentLevel] = useState<DrillLevel>({ type: 'DISTRICT' });
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [breadcrumbs, setBreadcrumbs] = useState<DrillLevel[]>([{ type: 'DISTRICT' }]);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  useEffect(() => { fetchData(); }, [currentLevel, currentPage]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let response;
      switch (currentLevel.type) {
        case 'DISTRICT':
          response = await apiService.getPennyDropDistrictDrillDown();
          setData(response.data || []);
          break;
        case 'MANDAL':
          response = await apiService.getPennyDropMandalDrillDown(currentLevel.district!);
          setData(response.data || []);
          break;
        case 'VILLAGE':
          response = await apiService.getPennyDropVillageDrillDown(currentLevel.district!, currentLevel.mandal!);
          setData(response.data || []);
          break;
        case 'BENEFICIARY':
          response = await apiService.getPennyDropBeneficiaryDrillDown(
            currentLevel.district!, currentLevel.mandal!, currentLevel.village!,
            { page: currentPage, limit: 50 }
          );
          setData(response.beneficiaries || []);
          if (response.pagination) {
            setTotalPages(response.pagination.totalPages);
            setTotalRecords(response.pagination.total);
          }
          break;
      }
    } catch (error) {
      console.error('Error fetching penny drop data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleDrillDown = (item: any) => {
    let newLevel: DrillLevel;
    if (currentLevel.type === 'DISTRICT') {
      newLevel = { type: 'MANDAL', district: item.district };
    } else if (currentLevel.type === 'MANDAL') {
      newLevel = { type: 'VILLAGE', district: currentLevel.district, mandal: item.mandal };
    } else if (currentLevel.type === 'VILLAGE') {
      newLevel = { type: 'BENEFICIARY', district: currentLevel.district, mandal: currentLevel.mandal, village: item.village };
    } else return;

    setCurrentLevel(newLevel);
    setBreadcrumbs([...breadcrumbs, newLevel]);
    setCurrentPage(1);
  };

  const handleBreadcrumbClick = (index: number) => {
    const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
    setBreadcrumbs(newBreadcrumbs);
    setCurrentLevel(newBreadcrumbs[newBreadcrumbs.length - 1]);
    setCurrentPage(1);
  };

  const getBreadcrumbName = (crumb: DrillLevel): string => {
    switch (crumb.type) {
      case 'DISTRICT': return 'All Districts';
      case 'MANDAL': return crumb.district || '';
      case 'VILLAGE': return crumb.mandal || '';
      case 'BENEFICIARY': return crumb.village || '';
      default: return '';
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params: { district?: string; mandal?: string; village?: string } = {};
      if (currentLevel.district) params.district = currentLevel.district;
      if (currentLevel.mandal) params.mandal = currentLevel.mandal;
      if (currentLevel.village) params.village = currentLevel.village;

      const blob = await apiService.exportPennyDropDrillDown(params);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      let fileName = 'PennyDrop_All_Districts.xlsx';
      if (currentLevel.type === 'MANDAL') fileName = `PennyDrop_Mandals_${currentLevel.district}.xlsx`;
      else if (currentLevel.type === 'VILLAGE') fileName = `PennyDrop_Villages_${currentLevel.district}_${currentLevel.mandal}.xlsx`;
      else if (currentLevel.type === 'BENEFICIARY') fileName = `PennyDrop_Beneficiaries_${currentLevel.village}.xlsx`;

      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    if (status === 'Success') return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Success</span>;
    if (status === 'Failed') return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Failed</span>;
    return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">Not Attempted</span>;
  };

  const renderAggregateTable = () => {
    const geoField = currentLevel.type === 'DISTRICT' ? 'district' : currentLevel.type === 'MANDAL' ? 'mandal' : 'village';

    const totals = data.reduce((acc, item) => ({
      totalBeneficiaries: acc.totalBeneficiaries + (item.totalBeneficiaries || 0),
      succeeded: acc.succeeded + (item.succeeded || 0),
      failed: acc.failed + (item.failed || 0),
      notAttempted: acc.notAttempted + (item.notAttempted || 0),
    }), { totalBeneficiaries: 0, succeeded: 0, failed: 0, notAttempted: 0 });

    return (
      <div className="overflow-x-auto bg-white rounded-lg shadow">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{currentLevel.type}</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Beneficiaries</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Succeeded</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Failed</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Not Attempted</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((item, index) => {
              return (
                <tr key={`${item[geoField]}-${index}`} className="hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => handleDrillDown(item)}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item[geoField]}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-700">{(item.totalBeneficiaries || 0).toLocaleString('en-IN')}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right"><span className="text-green-600 font-semibold">{(item.succeeded || 0).toLocaleString('en-IN')}</span></td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right"><span className="text-red-600 font-semibold">{(item.failed || 0).toLocaleString('en-IN')}</span></td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-500">{(item.notAttempted || 0).toLocaleString('en-IN')}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-center"><ChevronRight className="w-5 h-5 text-blue-600 mx-auto" /></td>
                </tr>
              );
            })}

            {data.length > 0 && (
              <tr className="bg-blue-50 font-bold border-t-2 border-blue-300">
                <td className="px-6 py-4 text-sm text-gray-900">TOTAL</td>
                <td className="px-6 py-4 text-sm text-right text-gray-900">{totals.totalBeneficiaries.toLocaleString('en-IN')}</td>
                <td className="px-6 py-4 text-sm text-right text-green-700">{totals.succeeded.toLocaleString('en-IN')}</td>
                <td className="px-6 py-4 text-sm text-right text-red-700">{totals.failed.toLocaleString('en-IN')}</td>
                <td className="px-6 py-4 text-sm text-right text-gray-600">{totals.notAttempted.toLocaleString('en-IN')}</td>
                <td className="px-6 py-4 text-center">-</td>
              </tr>
            )}
          </tbody>
        </table>
        {data.length === 0 && !loading && <div className="text-center py-12 text-gray-500">No data available</div>}
      </div>
    );
  };

  const renderBeneficiaryTable = () => (
    <div className="overflow-x-auto bg-white rounded-lg shadow">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Beneficiary ID</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Attempts</th>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Success</th>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Failed</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Response</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Reason</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.map((b: any, index: number) => (
            <tr key={b.beneficiaryId || index} className="hover:bg-gray-50">
              <td className="px-6 py-4 text-sm font-medium text-gray-900">{b.beneficiaryId}</td>
              <td className="px-6 py-4 text-sm text-gray-700">{b.applicantName}</td>
              <td className="px-6 py-4 text-center">{getStatusBadge(b.status)}</td>
              <td className="px-6 py-4 text-sm text-center text-gray-700">{b.totalAttempts}</td>
              <td className="px-6 py-4 text-sm text-center text-green-600 font-semibold">{b.successAttempts}</td>
              <td className="px-6 py-4 text-sm text-center text-red-600 font-semibold">{b.failedAttempts}</td>
              <td className="px-6 py-4 text-sm text-gray-700">{b.lastResponseCode || '-'}</td>
              <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">{b.lastRejectionReason || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {totalPages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <p className="text-sm text-gray-700">
            Showing <span className="font-medium">{(currentPage - 1) * 50 + 1}</span> to{' '}
            <span className="font-medium">{Math.min(currentPage * 50, totalRecords)}</span> of{' '}
            <span className="font-medium">{totalRecords}</span> results
          </p>
          <nav className="inline-flex rounded-md shadow-sm -space-x-px">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
              className="px-3 py-2 rounded-l-md border border-gray-300 bg-white text-sm text-gray-500 hover:bg-gray-50 disabled:opacity-50">Previous</button>
            <span className="px-4 py-2 border border-gray-300 bg-white text-sm text-gray-700">Page {currentPage} of {totalPages}</span>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
              className="px-3 py-2 rounded-r-md border border-gray-300 bg-white text-sm text-gray-500 hover:bg-gray-50 disabled:opacity-50">Next</button>
          </nav>
        </div>
      )}

      {data.length === 0 && !loading && <div className="text-center py-12 text-gray-500">No beneficiaries found</div>}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Penny Drop Report</h1>
            <p className="mt-1 text-sm text-gray-600">
              District → Mandal → Village → Beneficiaries
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={handleExport} disabled={exporting || loading || data.length === 0}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">
              <Download className={`w-4 h-4 mr-2 ${exporting ? 'animate-bounce' : ''}`} />
              {exporting ? 'Exporting...' : 'Export'}
            </button>
            <button onClick={fetchData} disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Breadcrumbs */}
        <div className="flex items-center space-x-2 mb-6 text-sm">
          <Home className="w-4 h-4 cursor-pointer text-blue-600 hover:text-blue-800" onClick={() => handleBreadcrumbClick(0)} />
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={index}>
              <ChevronRight className="w-4 h-4 text-gray-400" />
              <span className={`cursor-pointer transition-colors ${index === breadcrumbs.length - 1 ? 'text-gray-900 font-semibold' : 'text-blue-600 hover:text-blue-800'}`}
                onClick={() => handleBreadcrumbClick(index)}>
                {getBreadcrumbName(crumb)}
              </span>
            </React.Fragment>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
          </div>
        ) : currentLevel.type === 'BENEFICIARY' ? renderBeneficiaryTable() : renderAggregateTable()}
      </div>
    </div>
  );
};

export default PennyDropDrillDown;
